// src/lib/contexts/auth/components/AuthProvider.tsx

import React from "react";
import { logger, LOGS } from "../../../utils";
import { useEventBus } from "../../events";
import { TokenRefreshFailedError } from "../services/errors";
import { IOAuthService } from "../services/IOAuthService";
import {
    getAuthService,
    getAuthSession,
} from "../services/KeycloakOAuthService";
import { AuthContextValue } from "../types/auth";

const AuthProviderContext = React.createContext<AuthContextValue | null>(null);

export function useAuthContext(): AuthContextValue {
    const context = React.useContext(AuthProviderContext);

    if (!context) {
        throw new Error(
            "useAuthContext: Context must be used inside AuthProvider",
        );
    }

    return context;
}

export const AuthProvider: React.FC<React.PropsWithChildren> = ({
    children,
}) => {
    logger.info(LOGS.AuthProvider, `AuthProvider()`);

    const service = React.useMemo<IOAuthService>(() => getAuthService(), []);

    const session = React.useMemo(() => getAuthSession(), []);

    const eventBus = useEventBus();

    const isAuthenticated = React.useSyncExternalStore(
        session.subscribe,
        session.getSnapshot,
    );

    const tearingDownRef = React.useRef(false);

    const teardownAndSignOut = React.useCallback(
        async ({ source }: { source: string }): Promise<void> => {
            if (tearingDownRef.current || !service.isAuthenticated()) {
                return;
            }
            tearingDownRef.current = true;
            logger.warn(
                LOGS.AuthProvider,
                `Signing out (source: ${source}) → disconnecting services then signing out`,
            );

            try {
                // 1. Cleanup des services
                await new Promise<void>((resolve) => {
                    const onDone = () => {
                        clearTimeout(timer);
                        eventBus.off("services-cleanup-completed", onDone);
                        resolve();
                    };
                    const timer: ReturnType<typeof setTimeout> = setTimeout(
                        () => {
                            eventBus.off("services-cleanup-completed", onDone);
                            logger.warn(
                                LOGS.AuthProvider,
                                "services-cleanup timeout — proceeding to signOut anyway",
                            );
                            resolve();
                        },
                        3000,
                    );
                    eventBus.on("services-cleanup-completed", onDone);
                    eventBus.emit("services-cleanup");
                });

                // 2. Logout
                await service.signOut();
            } finally {
                tearingDownRef.current = false;
            }
        },
        [service, eventBus],
    );

    React.useEffect(() => {
        eventBus.on("auth-token-rejected", teardownAndSignOut);
        return () => eventBus.off("auth-token-rejected", teardownAndSignOut);
    }, [eventBus, teardownAndSignOut]);

    // Frontière unique : tout échec de refresh (TokenRefreshFailedError), qu'il survienne au build
    // d'un header (getAccessToken/getProviderToken) ou lors d'un forceRefresh post-401, émet
    // 'auth-token-rejected' (→ teardown) puis propage l'erreur pour interrompre le flux appelant.
    const wrapRefreshFailure = React.useCallback(
        <T,>(fn: () => Promise<T>, source: string): Promise<T> =>
            fn().catch((e: unknown) => {
                if (e instanceof TokenRefreshFailedError) {
                    eventBus.emit("auth-token-rejected", { source });
                }
                throw e;
            }),
        [eventBus],
    );

    const context = React.useMemo<AuthContextValue>(() => {
        return {
            isAuthenticated,

            login: () => service.login(),
            logout: () => teardownAndSignOut({ source: "logout" }),

            loginRedirect: () => service.loginRedirect(),
            logoutRedirect: () =>
                teardownAndSignOut({ source: "logoutRedirect" }),

            getAccessToken: () =>
                wrapRefreshFailure(
                    () => service.getAccessToken(),
                    "getAccessToken",
                ),
            getProviderToken: () =>
                wrapRefreshFailure(
                    () => service.getProviderToken(),
                    "getProviderToken",
                ),
            getBrokeredProvider: () => service.getBrokeredProvider(),

            forceRefresh: () =>
                wrapRefreshFailure(
                    () => service.refreshTokens(),
                    "forceRefresh",
                ),

            getUser: () => service.getUser(),
        };
    }, [service, isAuthenticated, wrapRefreshFailure, teardownAndSignOut]);

    return (
        <AuthProviderContext.Provider value={context}>
            {children}
        </AuthProviderContext.Provider>
    );
};
